import React from "react";
import Box from "@mui/material/Box";

/**
 * LoginBackground — full-screen dark gradient + animated radar SVG.
 * Matches the landing CTA section visual language so login feels native to medlift.app.
 *
 * Renders:
 *   1. Slate-950 → slate-900 gradient base
 *   2. Subtle teal ambient glow (blurred radial)
 *   3. Animated radar SVG positioned at the bottom, partially below the fold
 *   4. Inline <style> with @keyframes for the radar sweep + blips (scoped via prefixed class names)
 *
 * Usage: place as the first child of a `position: relative; min-height: 100vh` container,
 * with `position: absolute; inset: 0; pointer-events: none` so it sits behind the form.
 */
export default function LoginBackground() {
  return (
    <>
      <style>{KEYFRAMES_CSS}</style>

      {/* Base gradient */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, #020617 0%, #0f172a 50%, #020617 100%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Ambient teal glow */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          height: 600,
          backgroundColor: "rgba(79, 209, 197, 0.06)",
          borderRadius: "50%",
          filter: "blur(150px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Radar SVG */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 600 600"
          fill="none"
          sx={{
            width: { xs: 700, md: 1100, lg: 1300 },
            height: { xs: 700, md: 1100, lg: 1300 },
            transform: "translateY(45%)",
            opacity: 0.95,
          }}
        >
          {/* Range rings */}
          <circle cx="300" cy="300" r="60" stroke="rgba(79,209,197,0.06)" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="120" stroke="rgba(79,209,197,0.06)" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="180" stroke="rgba(79,209,197,0.08)" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="240" stroke="rgba(79,209,197,0.06)" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="290" stroke="rgba(79,209,197,0.04)" strokeWidth="1" fill="none" />

          {/* Crosshairs */}
          <line x1="300" y1="10" x2="300" y2="590" stroke="rgba(79,209,197,0.04)" strokeWidth="1" />
          <line x1="10" y1="300" x2="590" y2="300" stroke="rgba(79,209,197,0.04)" strokeWidth="1" />
          <line x1="95" y1="95" x2="505" y2="505" stroke="rgba(79,209,197,0.03)" strokeWidth="1" />
          <line x1="505" y1="95" x2="95" y2="505" stroke="rgba(79,209,197,0.03)" strokeWidth="1" />

          {/* Center marker */}
          <circle cx="300" cy="300" r="4" fill="#4fd1c5" opacity="0.8" />
          <circle cx="300" cy="300" r="8" fill="none" stroke="#4fd1c5" strokeWidth="1" opacity="0.4" />

          {/* Sweep arm */}
          <defs>
            <linearGradient id="ml-sweepGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(79,209,197,0)" />
              <stop offset="100%" stopColor="rgba(79,209,197,0.15)" />
            </linearGradient>
          </defs>
          <g style={{ transformOrigin: "300px 300px", animation: "ml-radar-sweep 4s linear infinite" }}>
            <path d="M300 300 L300 10 A290 290 0 0 1 445 45 Z" fill="url(#ml-sweepGrad)" opacity="0.6" />
            <line x1="300" y1="300" x2="300" y2="10" stroke="#4fd1c5" strokeWidth="1.5" opacity="0.6" />
          </g>

          {/* Blips */}
          <g style={{ animation: "ml-radar-blip-1 4s linear infinite" }}>
            <circle cx="390" cy="195" r="5" fill="#4fd1c5" />
          </g>
          <g style={{ animation: "ml-radar-blip-2 4s linear infinite" }}>
            <circle cx="145" cy="270" r="5" fill="#4fd1c5" />
          </g>
          <g style={{ animation: "ml-radar-blip-3 4s linear infinite" }}>
            <circle cx="420" cy="400" r="5" fill="#4fd1c5" />
          </g>
          <g style={{ animation: "ml-radar-blip-4 4s linear infinite" }}>
            <circle cx="230" cy="230" r="5" fill="#4fd1c5" />
          </g>
          <g style={{ animation: "ml-radar-blip-3 4s linear infinite 0.4s" }}>
            <circle cx="160" cy="420" r="4" fill="#4fd1c5" />
          </g>
          <g style={{ animation: "ml-radar-blip-1 4s linear infinite 0.3s" }}>
            <circle cx="470" cy="240" r="3.5" fill="#4fd1c5" />
          </g>
        </Box>
      </Box>

      {/* Dark overlay for text readability over the radar */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(2,6,23,0.5) 0%, rgba(2,6,23,0.3) 50%, rgba(2,6,23,0.6) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
    </>
  );
}

const KEYFRAMES_CSS = `
@keyframes ml-radar-sweep {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes ml-radar-blip-1 {
  0%, 16% { opacity: 0; }
  18% { opacity: 1; }
  90% { opacity: 0.7; }
  96%, 100% { opacity: 0; }
}
@keyframes ml-radar-blip-2 {
  0%, 36% { opacity: 0; }
  38% { opacity: 1; }
  90% { opacity: 0.7; }
  96%, 100% { opacity: 0; }
}
@keyframes ml-radar-blip-3 {
  0%, 56% { opacity: 0; }
  58% { opacity: 1; }
  90% { opacity: 0.6; }
  96%, 100% { opacity: 0; }
}
@keyframes ml-radar-blip-4 {
  0%, 81% { opacity: 0; }
  83% { opacity: 1; }
  96% { opacity: 0.6; }
  100% { opacity: 0; }
}
@keyframes ml-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  [class*="ml-radar"], [style*="ml-radar"], [style*="ml-fade"] { animation: none !important; }
}
`;
